import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '@/shared/components/Dialog';
import { DatePickerField } from '@/shared/components/DatePickerField';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { Tooltip } from '@/shared/components/Tooltip';
import { Checkbox } from '@/shared/components/Checkbox';
import { TextField } from '@/shared/components/TextField';
import { RESCHEDULE_SR_LABELS as L } from '../../constants/labels';
import { SERVICE_MANAGEMENT_LABELS as TL } from '../../constants/translations/labels';
import { EligibleTechnicianDropdown } from './EligibleTechnicianDropdown';
import RescheduleConfirmationStep from './RescheduleConfirmationStep';
import {
  formatScheduledDate,
  formatScheduledTime,
} from './viewServiceRequestHelpers';
import type {
  ReschedulePreviousSnapshot,
  RescheduleAssignToRole,
} from '../../hooks/useReschedule';

// ----------------------------------------------------------------------------
// Shared inline helpers (mirrors AssignTechnicianModal). Duplicated here to
// keep the assign modal untouched; a small follow-up could lift these into a
// `scheduleWindowFields.tsx` and have both modals consume them.
// ----------------------------------------------------------------------------

const OptionalSuffix: React.FC = () => (
  <span
    style={{
      fontFamily: 'Roboto',
      fontSize: 12,
      lineHeight: '16px',
      fontWeight: 400,
      letterSpacing: 0.4,
      color: '#474A4C',
      marginLeft: 4,
    }}
  >
    {L.FIELD_TIME_OPTIONAL}
  </span>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h3
    style={{
      fontFamily: 'Roboto',
      fontSize: 18,
      lineHeight: '24px',
      fontWeight: 400,
      letterSpacing: 0.1,
      color: '#474A4C',
    }}
  >
    {children}
  </h3>
);

const ClockIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="#181A1B" strokeWidth="2" />
    <path
      d="M12 7v5l3 2"
      stroke="#181A1B"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const parseTime = (value: string): { hour: number; minute: number } => {
  const [hStr = '0', mStr = '0'] = (value || '').split(':');
  const hour = Math.max(0, Math.min(23, Number(hStr) || 0));
  const minute = Math.max(0, Math.min(59, Number(mStr) || 0));
  return { hour, minute };
};
const formatTime = (hour: number, minute: number): string => {
  const h = String(hour).padStart(2, '0');
  const m = String(minute).padStart(2, '0');
  return `${h}:${m}`;
};

const TimeNumberBox: React.FC<{
  value: number;
  max: number;
  active: boolean;
  caption: string;
  ariaLabel: string;
  testId: string;
  onChange: (next: number) => void;
  onFocus: () => void;
}> = ({
  value,
  max,
  active,
  caption,
  ariaLabel,
  testId,
  onChange,
  onFocus,
}) => {
  const [text, setText] = useState(() => String(value).padStart(2, '0'));
  const focusedRef = useRef(false);
  useEffect(() => {
    if (!focusedRef.current) {
      setText(String(value).padStart(2, '0'));
    }
  }, [value]);

  const handleChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 2);
    if (digits === '') {
      setText('');
      onChange(0);
      return;
    }
    const num = Math.min(max, Number(digits));
    setText(String(num));
    onChange(num);
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={text}
        onFocus={(e) => {
          focusedRef.current = true;
          e.target.select();
          onFocus();
        }}
        onBlur={() => {
          focusedRef.current = false;
          setText(String(value).padStart(2, '0'));
        }}
        onChange={(e) => handleChange(e.target.value)}
        aria-label={ariaLabel}
        data-testid={testId}
        className="w-22 text-center outline-none"
        style={{
          height: 50,
          borderRadius: 8,
          backgroundColor: active ? '#E8EEF2' : '#F2F4F5',
          border: active ? '2px solid #2A4455' : '2px solid transparent',
          fontFamily: 'Roboto',
          fontSize: 24,
          lineHeight: '32px',
          fontWeight: 400,
          color: '#181A1B',
          letterSpacing: 0,
        }}
      />
      <span
        style={{
          fontFamily: 'Roboto',
          fontSize: 12,
          lineHeight: '16px',
          fontWeight: 400,
          letterSpacing: 0.4,
          color: '#474A4C',
        }}
      >
        {caption}
      </span>
    </div>
  );
};

const TimeField: React.FC<{
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  testId?: string;
}> = ({
  label,
  value,
  onChange,
  onOpenChange,
  disabled = false,
  error = false,
  errorMessage,
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const [draftHour, setDraftHour] = useState(0);
  const [draftMinute, setDraftMinute] = useState(0);
  const [activeField, setActiveField] = useState<'hour' | 'minute'>('hour');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { hour, minute } = parseTime(value);
  const displayValue = value ? formatTime(hour, minute) : '';
  const ariaLabel = typeof label === 'string' ? label : undefined;

  const setOpenState = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapperRef.current) {
        return;
      }
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpenState(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openPopup = () => {
    if (disabled) {
      return;
    }
    setDraftHour(hour);
    setDraftMinute(minute);
    setActiveField('hour');
    setOpenState(true);
  };
  const closePopup = () => setOpenState(false);
  const confirm = () => {
    onChange(formatTime(draftHour, draftMinute));
    setOpenState(false);
  };

  return (
    <div className="flex-1 relative" ref={wrapperRef}>
      <div
        className={`relative w-full h-14 bg-[#E5E7E9] rounded-lg flex items-center pl-4 pr-3 gap-2 transition-colors ${
          disabled
            ? 'opacity-60 cursor-not-allowed'
            : 'hover:bg-[#eceeef] cursor-pointer'
        }`}
        style={{
          borderBottom: error
            ? '2px solid #E11B1B'
            : open
              ? '2px solid #2a4455'
              : 'none',
        }}
        onClick={openPopup}
        onKeyDown={(e) => {
          if (disabled) {
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPopup();
          }
        }}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
        data-testid={testId}
      >
        <div
          className="flex-1 relative flex flex-col justify-center"
          style={{ minHeight: 40 }}
        >
          {(() => {
            const labelFloating = open || Boolean(value);
            const labelColor = open ? '#2a4455' : '#474A4C';
            return (
              <span
                className="absolute left-0 pointer-events-none origin-top-left"
                style={{
                  fontFamily: 'Roboto',
                  color: labelColor,
                  top: labelFloating ? 0 : 10,
                  fontSize: labelFloating ? 12 : 16,
                  lineHeight: labelFloating ? '16px' : '20px',
                  fontWeight: 400,
                  letterSpacing: 0.25,
                  transition:
                    'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                }}
              >
                {label}
              </span>
            );
          })()}
          {displayValue && (
            <span
              className="self-start"
              style={{
                fontFamily: 'Roboto',
                color: '#181A1B',
                fontSize: 16,
                lineHeight: '20px',
                fontWeight: 400,
                letterSpacing: 0.25,
                marginTop: 16,
              }}
            >
              {displayValue}
            </span>
          )}
        </div>
        {!open && !error && (
          <div
            className="absolute bottom-0"
            style={{
              left: '6px',
              right: '6px',
              height: '1px',
              backgroundColor: '#6a7278',
            }}
          />
        )}
        <div className="p-2 rounded-full hover:bg-black/5 transition-colors shrink-0">
          <ClockIcon />
        </div>
      </div>
      {open && (
        <div
          className="absolute left-0 right-0 mt-2 z-50 bg-white rounded-2xl"
          style={{
            top: '100%',
            padding: 16,
            border: '1px solid #DCDDE0',
            boxShadow: '0 4px 12px 0 rgba(0,0,0,0.12)',
          }}
          role="dialog"
          aria-label={L.TIME_DIALOG_TITLE}
        >
          <p
            style={{
              fontFamily: 'Roboto',
              fontSize: 12,
              lineHeight: '16px',
              fontWeight: 400,
              letterSpacing: 0.4,
              color: '#474A4C',
              marginBottom: 20,
            }}
          >
            {L.TIME_DIALOG_TITLE}
          </p>
          <div className="flex items-center justify-center gap-2">
            <TimeNumberBox
              value={draftHour}
              max={23}
              active={activeField === 'hour'}
              caption={L.TIME_DIALOG_HOUR}
              ariaLabel={L.TIME_DIALOG_HOUR}
              testId={testId ? `${testId}-hour-input` : 'time-hour-input'}
              onChange={setDraftHour}
              onFocus={() => setActiveField('hour')}
            />
            <span
              style={{
                fontFamily: 'Roboto',
                fontSize: 24,
                lineHeight: '32px',
                fontWeight: 400,
                color: '#181A1B',
                paddingBottom: 20,
              }}
            >
              :
            </span>
            <TimeNumberBox
              value={draftMinute}
              max={59}
              active={activeField === 'minute'}
              caption={L.TIME_DIALOG_MINUTE}
              ariaLabel={L.TIME_DIALOG_MINUTE}
              testId={testId ? `${testId}-minute-input` : 'time-minute-input'}
              onChange={setDraftMinute}
              onFocus={() => setActiveField('minute')}
            />
          </div>
          <div className="flex items-center justify-end gap-2 mt-2">
            <button
              type="button"
              onClick={closePopup}
              className="h-9 px-3 rounded-md text-sm font-medium tracking-[0.1px] text-[#2A4455] hover:bg-[#F3F4F6] transition-colors cursor-pointer"
            >
              {L.BUTTON_CANCEL}
            </button>
            <button
              type="button"
              onClick={confirm}
              className="h-9 px-3 rounded-md text-sm font-medium tracking-[0.1px] text-[#2A4455] hover:bg-[#F3F4F6] transition-colors cursor-pointer"
            >
              {L.TIME_DIALOG_OK}
            </button>
          </div>
        </div>
      )}
      {error && errorMessage && (
        <p
          className="mt-1"
          style={{
            fontFamily: 'Roboto',
            fontSize: 12,
            lineHeight: '16px',
            letterSpacing: 0.4,
            color: '#E11B1B',
          }}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Current-visit summary card (top of the form step).
// ----------------------------------------------------------------------------

const ReadOnlyField: React.FC<{
  label: string;
  value: string;
}> = ({ label, value }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <span
      style={{
        fontFamily: 'Roboto',
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 400,
        letterSpacing: 0.4,
        color: '#474A4C',
      }}
    >
      {label}
    </span>
    <span
      className="break-words"
      style={{
        fontFamily: 'Roboto',
        fontSize: 16,
        lineHeight: '24px',
        fontWeight: 400,
        color: '#181A1B',
      }}
    >
      {value || '—'}
    </span>
  </div>
);

interface CurrentVisitSummary {
  scheduledStartAt: string | null | undefined;
  scheduledEndAt: string | null | undefined;
  taskFormId: string | null | undefined;
  taskFormName: string | null | undefined;
  certificateId: string | null | undefined;
  certificateName: string | null | undefined;
  serviceTypeName: string | null | undefined;
  technicianName: string | null | undefined;
  clientName: string | null | undefined;
}

// formatScheduledDate / formatScheduledTime emit Europe/Warsaw wall-clock
// regardless of the browser's local timezone — required so what the admin
// sees in the read-only card matches the value the BE stored (and what we
// prefill into the editable fields below).
const fmtDateFromIso = (iso: string | null | undefined): string => {
  const v = formatScheduledDate(iso);
  return v === '—' || v === '-' ? '' : v;
};
const fmtTimeFromIso = (iso: string | null | undefined): string => {
  const v = formatScheduledTime(iso);
  return v === '—' || v === '-' ? '' : v;
};

const CurrentVisitCard: React.FC<{ summary: CurrentVisitSummary }> = ({
  summary,
}) => (
  <div
    className="rounded-lg border border-[#DCDDE0]"
    style={{
      backgroundColor: '#FFFFFF',
      padding: 16,
    }}
    data-testid="reschedule-current-visit-card"
  >
    <h3
      style={{
        fontFamily: 'Roboto',
        fontSize: 18,
        lineHeight: '24px',
        fontWeight: 500,
        letterSpacing: 0.1,
        color: '#474A4C',
        marginBottom: 16,
      }}
    >
      {L.SECTION_CURRENT}
    </h3>
    <div className="grid grid-cols-2 gap-x-16 gap-y-8">
      <ReadOnlyField
        label={L.FIELD_START_DATE}
        value={fmtDateFromIso(summary.scheduledStartAt)}
      />
      <ReadOnlyField
        label={L.FIELD_END_DATE}
        value={fmtDateFromIso(summary.scheduledEndAt)}
      />
      <ReadOnlyField
        label={L.FIELD_START_TIME}
        value={fmtTimeFromIso(summary.scheduledStartAt)}
      />
      <ReadOnlyField
        label={L.FIELD_END_TIME}
        value={fmtTimeFromIso(summary.scheduledEndAt)}
      />
      <ReadOnlyField
        label={TL.FIELD_TASK_FORM_ID.polish}
        value={summary.taskFormId ?? ''}
      />
      <ReadOnlyField
        label={TL.FIELD_TASK_FORM_NAME.polish}
        value={summary.taskFormName ?? ''}
      />
      <ReadOnlyField
        label={L.FIELD_CERTIFICATE_ID}
        value={summary.certificateId ?? ''}
      />
      <ReadOnlyField
        label={TL.FIELD_CERTIFICATE_TEMPLATE_NAME.polish}
        value={summary.certificateName ?? ''}
      />
      <ReadOnlyField
        label={L.FIELD_SERVICE_TYPE}
        value={summary.serviceTypeName ?? ''}
      />
      <ReadOnlyField
        label={L.FIELD_ASSIGNED_USER}
        value={summary.technicianName ?? ''}
      />
      <div className="col-span-2">
        <ReadOnlyField
          label={L.FIELD_CLIENT_NAME}
          value={summary.clientName ?? ''}
        />
      </div>
    </div>
  </div>
);

// ----------------------------------------------------------------------------
// Main modal
// ----------------------------------------------------------------------------

export interface RescheduleModalProps {
  open: boolean;
  onClose: () => void;

  serviceRequestId: string | undefined;

  // Current-visit summary (read-only top card).
  summary: CurrentVisitSummary;

  // Step
  step: 'form' | 'confirm';
  onGoToConfirm: () => void;
  onGoBackToForm: () => void;
  previousSnapshot: ReschedulePreviousSnapshot | null;

  // Window
  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
  startDateIso: string;
  endDateIso: string;
  onStartDateChange: (d: Date | undefined) => void;
  onEndDateChange: (d: Date | undefined) => void;
  onStartTimeChange: (t: string) => void;
  onEndTimeChange: (t: string) => void;
  startDateError: string;
  endDateError: string;
  endTimeError: string;
  windowComplete: boolean;

  // Assignee
  assignToRole: RescheduleAssignToRole;
  onAssignToRoleChange: (role: RescheduleAssignToRole) => void;
  assignToError: string;

  // Technician
  selectedTechnicianId: string;
  selectedTechnicianCode: string;
  selectedTechnicianName: string;
  onSelectTechnician: (
    id: string,
    technicianCode: string,
    name?: string
  ) => void;
  technicianError: string;
  prefillTechnicianName: string;

  // Admin (reassign-to-admin during reschedule)
  selectedAdminId: string;
  selectedAdminCode: string;
  selectedAdminName: string;
  onSelectAdmin: (id: string, adminCode: string, name?: string) => void;
  adminError: string;

  // Notify toggle (confirm step)
  notify: boolean;
  onNotifyChange: (value: boolean) => void;

  // Submission
  isSubmitting: boolean;
  submitError: string;
  canSubmit: boolean;
  onSubmit: () => void;

  /**
   * When `false`, hides the "Przydziel do" (Technik/Admin) role dropdown and
   * renders the technician picker directly — used for the technician
   * self-reschedule flow, where the assignee is always a technician and
   * picking one is optional (blank = keep the current assignee). Defaults to
   * `true` (admin behavior, unchanged).
   */
  showAssignToRolePicker?: boolean;
}

const RescheduleModal: React.FC<RescheduleModalProps> = ({
  open,
  onClose,
  serviceRequestId,
  summary,
  step,
  onGoToConfirm,
  onGoBackToForm,
  previousSnapshot,
  startDate,
  endDate,
  startTime,
  endTime,
  startDateIso,
  endDateIso,
  onStartDateChange,
  onEndDateChange,
  onStartTimeChange,
  onEndTimeChange,
  startDateError,
  endDateError,
  endTimeError,
  windowComplete,
  assignToRole,
  onAssignToRoleChange,
  assignToError,
  selectedTechnicianId,
  selectedTechnicianCode,
  selectedTechnicianName,
  onSelectTechnician,
  technicianError,
  selectedAdminId,
  selectedAdminCode,
  selectedAdminName,
  onSelectAdmin,
  adminError,
  notify,
  onNotifyChange,
  isSubmitting,
  submitError,
  canSubmit,
  onSubmit,
  showAssignToRolePicker = true,
}) => {
  const windowReady = windowComplete && !endDateError && !endTimeError;

  const scrollContentRef = useRef<HTMLDivElement>(null);
  const assignToWrapperRef = useRef<HTMLDivElement>(null);
  const dateRowRef = useRef<HTMLDivElement>(null);
  const timeRowRef = useRef<HTMLDivElement>(null);
  const technicianWrapperRef = useRef<HTMLDivElement>(null);
  const adminWrapperRef = useRef<HTMLDivElement>(null);
  const scrollBufferRef = useRef<HTMLDivElement>(null);
  const savedScrollTopRef = useRef<number | null>(null);

  // The confirm step reuses the same scrollable container as the form step —
  // without this, scrolling down on the form before clicking "Review" leaves
  // the confirm screen scrolled to that same position instead of the top.
  useEffect(() => {
    if (step !== 'confirm') {
      return;
    }
    const container = scrollContentRef.current;
    if (!container) {
      return;
    }
    container.scrollTop = 0;
  }, [step]);

  // Submit-time errors (e.g. the 24h-lead-time rule) render below the form
  // fields, past the fold — scroll them into view so the user isn't left
  // staring at an unchanged screen with no visible feedback.
  useEffect(() => {
    if (!submitError) {
      return;
    }
    const container = scrollContentRef.current;
    if (!container) {
      return;
    }
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, [submitError]);

  const handleAssignToOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      return;
    }
    requestAnimationFrame(() => {
      const container = scrollContentRef.current;
      const wrapper = assignToWrapperRef.current;
      if (!container || !wrapper) {
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      // Bring the bottom of the trigger to about 1/3 down the viewport so the
      // menu (rendered below) stays inside the scrollable area.
      const desiredTriggerBottom = containerRect.top + containerRect.height / 3;
      const delta = wrapperRect.bottom - desiredTriggerBottom;
      if (delta > 0) {
        container.scrollBy({ top: delta, behavior: 'smooth' });
      }
    });
  };

  // When a date or time picker opens, its popup needs viewport room below
  // the trigger that the modal's natural layout doesn't always provide. We
  // synchronously (a) expand a normally-collapsed buffer so the body has
  // enough overflow, then (b) scroll the trigger row close to the top of
  // the scrollable body. Both must happen before React commits the picker
  // mount, so useDatePickerPosition's useLayoutEffect sees the post-scroll
  // trigger position on its first compute. On close we restore both — the
  // user never sees the buffer's whitespace. Each picker can request a
  // different buffer height so the calendar (~480px) and the time popup
  // (~180px) don't pay the same scroll cost.
  const handlePickerOpenChange = (
    isOpen: boolean,
    rowRef: React.RefObject<HTMLDivElement | null>,
    bufferHeight: number
  ) => {
    const container = scrollContentRef.current;
    const buffer = scrollBufferRef.current;
    if (!container) {
      return;
    }
    if (isOpen) {
      if (buffer) {
        buffer.style.height = `${bufferHeight}px`;
      }
      savedScrollTopRef.current = container.scrollTop;
      const row = rowRef.current;
      if (!row) {
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      const delta = rowRect.top - containerRect.top - 8;
      if (delta > 0) {
        container.scrollTop += delta;
      }
      return;
    }
    if (savedScrollTopRef.current !== null) {
      container.scrollTop = savedScrollTopRef.current;
      savedScrollTopRef.current = null;
    }
    if (buffer) {
      buffer.style.height = '0px';
    }
  };

  const handleDatePickerOpenChange = (isOpen: boolean) =>
    handlePickerOpenChange(isOpen, dateRowRef, 320);
  const handleTimePickerOpenChange = (isOpen: boolean) =>
    handlePickerOpenChange(isOpen, timeRowRef, 80);
  const handleTechnicianOpenChange = (isOpen: boolean) =>
    handlePickerOpenChange(isOpen, technicianWrapperRef, 300);
  const handleAdminOpenChange = (isOpen: boolean) =>
    handlePickerOpenChange(isOpen, adminWrapperRef, 300);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={700}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      className="overflow-visible!"
      data-testid="reschedule-modal"
    >
      <div
        className="flex flex-col relative"
        style={{ height: 636, maxHeight: 'calc(100vh - 48px)' }}
      >
        <div className="px-6 pt-6 pb-0 shrink-0 flex flex-col gap-2">
          <h2
            style={{
              fontFamily: 'Roboto',
              fontSize: 22,
              lineHeight: '28px',
              fontWeight: 400,
              letterSpacing: 0,
              color: '#181A1B',
            }}
            data-testid="reschedule-modal-title"
          >
            {step === 'form' ? L.TITLE : TL.RESCHEDULE_CONFIRM_TITLE.polish}
          </h2>
          {step === 'confirm' && (
            <p
              style={{
                fontFamily: 'Roboto',
                fontSize: 14,
                lineHeight: '20px',
                fontWeight: 400,
                letterSpacing: 0.25,
                color: '#474A4C',
              }}
              data-testid="reschedule-modal-subtitle"
            >
              {TL.RESCHEDULE_CONFIRM_SUBTITLE.polish}
            </p>
          )}
        </div>

        <div
          ref={scrollContentRef}
          className="px-6 pt-4 pb-6 flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto"
        >
          {step === 'form' ? (
            <>
              <CurrentVisitCard summary={summary} />

              {/* Nowy termin (schedule) */}
              {/* marginTop adds 20px on top of the parent's 12px flex gap to
                  reach the required 32px gap below the current-visit card. */}
              <div className="flex flex-col gap-2" style={{ marginTop: 20 }}>
                <SectionHeading>{L.SECTION_NEW}</SectionHeading>
                <div className="flex gap-4" ref={dateRowRef}>
                  <DatePickerField
                    label={L.FIELD_START_DATE}
                    value={startDate}
                    onChange={onStartDateChange}
                    onOpenChange={handleDatePickerOpenChange}
                    variant="flat"
                    labelFontSize={16}
                    disabled={isSubmitting}
                    minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                    ariaLabel={L.FIELD_START_DATE}
                    error={Boolean(startDateError)}
                    errorMessage={startDateError || undefined}
                    dataTestId="reschedule-start-date"
                  />
                  <DatePickerField
                    label={L.FIELD_END_DATE}
                    value={endDate}
                    onChange={onEndDateChange}
                    onOpenChange={handleDatePickerOpenChange}
                    variant="flat"
                    labelFontSize={16}
                    disabled={isSubmitting}
                    minDate={
                      startDate
                        ? new Date(new Date(startDate).setHours(0, 0, 0, 0))
                        : new Date(new Date().setHours(0, 0, 0, 0))
                    }
                    ariaLabel={L.FIELD_END_DATE}
                    error={Boolean(endDateError)}
                    errorMessage={endDateError || undefined}
                    dataTestId="reschedule-end-date"
                  />
                </div>
                <div
                  className="flex gap-4"
                  style={{ marginTop: 24 }}
                  ref={timeRowRef}
                >
                  <TimeField
                    label={
                      <>
                        {L.FIELD_START_TIME}
                        <OptionalSuffix />
                      </>
                    }
                    value={startTime}
                    onChange={onStartTimeChange}
                    onOpenChange={handleTimePickerOpenChange}
                    disabled={isSubmitting}
                    testId="reschedule-start-time"
                  />
                  <TimeField
                    label={
                      <>
                        {L.FIELD_END_TIME}
                        <OptionalSuffix />
                      </>
                    }
                    value={endTime}
                    onChange={onEndTimeChange}
                    onOpenChange={handleTimePickerOpenChange}
                    disabled={isSubmitting}
                    error={Boolean(endTimeError)}
                    {...(endTimeError ? { errorMessage: endTimeError } : {})}
                    testId="reschedule-end-time"
                  />
                </div>
              </div>

              <div
                className="border-t border-[#DCDDE0]"
                role="separator"
                aria-hidden="true"
                style={{ marginTop: 32, marginBottom: 32 }}
                data-testid="reschedule-schedule-assignee-divider"
              />

              {/* Assignee */}
              <div
                className="flex flex-col gap-8"
                data-field="reschedule-technician-picker"
              >
                {/* Nested gap-2 keeps the heading-to-field gap at 8px,
                    independent of the outer container's 32px gap (which
                    still separates this block from the ID rows below). */}
                <div className="flex flex-col gap-2">
                  <SectionHeading>{L.SECTION_ASSIGNEE}</SectionHeading>
                  {!showAssignToRolePicker ? (
                    <div className="flex flex-wrap gap-4 items-start">
                      <div
                        className="min-w-0 flex-1 max-w-[316px]"
                        ref={technicianWrapperRef}
                      >
                        <EligibleTechnicianDropdown
                          serviceRequestId={serviceRequestId}
                          startDate={startDateIso}
                          endDate={endDateIso}
                          startTime={startTime}
                          endTime={endTime}
                          windowReady={windowReady}
                          selectedId={selectedTechnicianId}
                          selectedName={selectedTechnicianName}
                          selectedCode={selectedTechnicianCode}
                          onSelect={onSelectTechnician}
                          onOpenChange={handleTechnicianOpenChange}
                          label={L.FIELD_TECHNICIAN}
                          disabled={isSubmitting}
                          error={windowReady && Boolean(technicianError)}
                          excludeRejected={false}
                          {...(windowReady && technicianError
                            ? { errorMessage: technicianError }
                            : {})}
                        />
                      </div>
                      {selectedTechnicianId && (
                        <div className="min-w-0 flex-1 max-w-[316px]">
                          <TextField
                            label={L.FIELD_TECHNICIAN_ID}
                            value={selectedTechnicianCode}
                            disabled
                            fullWidth
                            dataTestId="reschedule-technician-id"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                      <div className="min-w-0" ref={assignToWrapperRef}>
                        {(() => {
                          const assignToDisabled = isSubmitting || !windowReady;
                          const assignToDropdown = (
                            <Dropdown
                              menuFullWidth
                              placement="bottom-start"
                              onOpenChange={handleAssignToOpenChange}
                              items={[
                                {
                                  id: 'technician',
                                  label: L.ASSIGN_TO_TECHNICIAN,
                                  selected: assignToRole === 'technician',
                                },
                                {
                                  id: 'admin',
                                  label: L.ASSIGN_TO_ADMIN,
                                  selected: assignToRole === 'admin',
                                },
                              ]}
                              {...(assignToRole
                                ? { selectedId: assignToRole }
                                : {})}
                              onItemClick={(id) =>
                                onAssignToRoleChange(
                                  id as RescheduleAssignToRole
                                )
                              }
                              disabled={assignToDisabled}
                              error={Boolean(assignToError)}
                              {...(assignToError
                                ? { errorMessage: assignToError }
                                : {})}
                              trigger={({
                                open,
                                toggle,
                                disabled: d,
                                error: e,
                              }) => (
                                <DropDownButton
                                  label={L.FIELD_ASSIGN_TO}
                                  value={
                                    assignToRole === 'technician'
                                      ? L.ASSIGN_TO_TECHNICIAN
                                      : assignToRole === 'admin'
                                        ? L.ASSIGN_TO_ADMIN
                                        : ''
                                  }
                                  open={open}
                                  toggle={toggle}
                                  disabled={d}
                                  error={e}
                                  data-testid="reschedule-assign-to"
                                />
                              )}
                            />
                          );
                          if (!isSubmitting && !windowReady) {
                            return (
                              <Tooltip
                                content={L.TECHNICIAN_TOOLTIP_NO_WINDOW}
                                forceShow
                                side="bottom"
                                align="left"
                                cursor="not-allowed"
                                // Keep this below the date-picker calendar's
                                // z-50 portal (both render to document.body) so
                                // an open calendar isn't covered by this tooltip.
                                zIndex={500}
                              >
                                {assignToDropdown}
                              </Tooltip>
                            );
                          }
                          return assignToDropdown;
                        })()}
                      </div>
                      {assignToRole === 'technician' && (
                        <div className="min-w-0" ref={technicianWrapperRef}>
                          <EligibleTechnicianDropdown
                            serviceRequestId={serviceRequestId}
                            startDate={startDateIso}
                            endDate={endDateIso}
                            startTime={startTime}
                            endTime={endTime}
                            windowReady={windowReady}
                            selectedId={selectedTechnicianId}
                            selectedName={selectedTechnicianName}
                            selectedCode={selectedTechnicianCode}
                            onSelect={onSelectTechnician}
                            onOpenChange={handleTechnicianOpenChange}
                            label={L.FIELD_TECHNICIAN}
                            disabled={isSubmitting}
                            error={windowReady && Boolean(technicianError)}
                            excludeRejected={false}
                            {...(windowReady && technicianError
                              ? { errorMessage: technicianError }
                              : {})}
                          />
                        </div>
                      )}
                      {assignToRole === 'admin' && (
                        <div className="min-w-0" ref={adminWrapperRef}>
                          <EligibleTechnicianDropdown
                            serviceRequestId={serviceRequestId}
                            startDate={startDateIso}
                            endDate={endDateIso}
                            startTime={startTime}
                            endTime={endTime}
                            windowReady={windowReady}
                            selectedId={selectedAdminId}
                            selectedName={selectedAdminName}
                            selectedCode={selectedAdminCode}
                            onSelect={onSelectAdmin}
                            onOpenChange={handleAdminOpenChange}
                            label={L.FIELD_ADMIN}
                            assigneeType="admin"
                            disabled={isSubmitting}
                            error={windowReady && Boolean(adminError)}
                            excludeRejected={false}
                            {...(windowReady && adminError
                              ? { errorMessage: adminError }
                              : {})}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {showAssignToRolePicker &&
                  assignToRole === 'technician' &&
                  selectedTechnicianId && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                      <div className="min-w-0">
                        <TextField
                          label={L.FIELD_TECHNICIAN_ID}
                          value={selectedTechnicianCode}
                          disabled
                          fullWidth
                          dataTestId="reschedule-technician-id"
                        />
                      </div>
                    </div>
                  )}
                {showAssignToRolePicker &&
                  assignToRole === 'admin' &&
                  selectedAdminId && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                      <div className="min-w-0">
                        <TextField
                          label={L.FIELD_ADMIN_ID}
                          value={selectedAdminCode}
                          disabled
                          fullWidth
                          dataTestId="reschedule-admin-id"
                        />
                      </div>
                    </div>
                  )}
              </div>

              {submitError && (
                <p
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: 12,
                    lineHeight: '16px',
                    letterSpacing: 0.4,
                    color: '#E11B1B',
                  }}
                  data-testid="reschedule-submit-error"
                >
                  {submitError}
                </p>
              )}
              {/* Scroll buffer: collapsed (0px) by default so the form
                  shows no trailing whitespace. The date/time picker open
                  handlers temporarily expand this to 320px so the body
                  has enough overflow for the trigger row to scroll near
                  the top, freeing viewport space for the popup. Collapsed
                  back to 0 on close, so the user never sees the space. */}
              <div
                ref={scrollBufferRef}
                aria-hidden="true"
                style={{ height: 0, flexShrink: 0 }}
              />
            </>
          ) : (
            <>
              <div
                className="flex items-start gap-3"
                data-testid="reschedule-notify-row"
              >
                <Checkbox
                  checked={notify}
                  onChange={onNotifyChange}
                  disabled={isSubmitting}
                  testId="reschedule-notify-checkbox"
                />
                <span
                  className="cursor-pointer select-none"
                  onClick={() => {
                    if (!isSubmitting) {
                      onNotifyChange(!notify);
                    }
                  }}
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: 14,
                    lineHeight: '20px',
                    fontWeight: 400,
                    letterSpacing: 0.25,
                    color: '#181A1B',
                  }}
                >
                  {TL.RESCHEDULE_CONFIRM_NOTIFY.polish}
                </span>
              </div>
              <RescheduleConfirmationStep
                previous={previousSnapshot}
                staticDetails={{
                  taskFormId: summary.taskFormId,
                  taskFormName: summary.taskFormName,
                  certificateId: summary.certificateId,
                  certificateName: summary.certificateName,
                  serviceTypeName: summary.serviceTypeName,
                  clientName: summary.clientName,
                }}
                next={{
                  technicianName:
                    assignToRole === 'admin'
                      ? selectedAdminName ||
                        previousSnapshot?.technicianName ||
                        ''
                      : selectedTechnicianName ||
                        previousSnapshot?.technicianName ||
                        '',
                  technicianCode:
                    assignToRole === 'admin'
                      ? selectedAdminCode
                      : selectedTechnicianCode,
                  startDate,
                  endDate,
                  startTime,
                  endTime,
                }}
              />
              {submitError && (
                <p
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: 12,
                    lineHeight: '16px',
                    letterSpacing: 0.4,
                    color: '#E11B1B',
                  }}
                  data-testid="reschedule-confirm-error"
                >
                  {submitError}
                </p>
              )}
            </>
          )}
        </div>

        <div
          className="flex items-center justify-end gap-3 px-6 pt-4 pb-6 shrink-0"
          data-dropdown-boundary
        >
          <button
            type="button"
            onClick={step === 'form' ? onClose : onGoBackToForm}
            disabled={isSubmitting}
            className={`h-10 px-5 rounded-lg border border-[#DCDDE0] bg-white text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              isSubmitting
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#F3F4F6] cursor-pointer'
            }`}
            data-testid="reschedule-secondary-button"
          >
            {step === 'form' ? L.BUTTON_CANCEL : L.BUTTON_BACK}
          </button>
          <button
            type="button"
            onClick={step === 'form' ? onGoToConfirm : onSubmit}
            disabled={step === 'form' ? isSubmitting : !canSubmit}
            className={`h-10 px-5 rounded-lg bg-[#2A4455] text-white text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              (step === 'form' ? !isSubmitting : canSubmit)
                ? 'hover:bg-[#1f3340] cursor-pointer'
                : 'opacity-50 cursor-not-allowed'
            }`}
            data-testid="reschedule-primary-button"
          >
            {step === 'form'
              ? L.BUTTON_REVIEW
              : isSubmitting
                ? L.BUTTON_SUBMITTING
                : TL.RESCHEDULE_BUTTON_SUBMIT.polish}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default RescheduleModal;
